import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { ConditionBadge, SealedStamp } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { BeforeAfterSlider } from "@/components/BeforeAfterSlider";
import { AcknowledgeReceipt } from "@/components/AcknowledgeReceipt";
import {
  MapPin, DollarSign, CalendarClock, Download, Loader2, ShieldCheck, DoorOpen, AlertTriangle, HeartPulse,
  Scale, FileText, Sparkles, ArrowRight,
} from "lucide-react";

const SCORE_STYLE = {
  "Excellent": "bg-[#D1FAE5] text-[#177245] border-[#A7F3D0]",
  "Minor issues": "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]",
  "Serious issues": "bg-[#FEE2E2] text-[#B91C1C] border-[#FECACA]",
  "Pending": "bg-[#F1F5F9] text-[#475569] border-[#E2E8F0]",
};

const RISK_STYLE = {
  none: "bg-[#D1FAE5] text-[#177245] border-[#A7F3D0]",
  low: "bg-[#D1FAE5] text-[#177245] border-[#A7F3D0]",
  medium: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]",
  high: "bg-[#FEE2E2] text-[#B91C1C] border-[#FECACA]",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return iso; }
}

export default function PublicReport() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "TenantSeal Evidence Report";
    const m = document.createElement("meta");
    m.name = "robots"; m.content = "noindex, nofollow";
    document.head.appendChild(m);
    const preview = params.get("preview") === "1" ? "?preview=1" : "";
    axios.get(`${API}/public/report/${token}${preview}`)
      .then(({ data }) => setData(data))
      .catch((err) => { console.error("public report load failed", err); setError(true); })
      .finally(() => setLoading(false));
    return () => { try { document.head.removeChild(m); } catch { /* noop */ } };
  }, [token]); // eslint-disable-line

  if (loading) {
    return <div className="min-h-screen grid place-items-center bg-[#EEF2F6]"><Loader2 className="animate-spin text-brand" size={24} /></div>;
  }
  if (error || !data) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#EEF2F6] px-6 text-center">
        <div>
          <Logo />
          <p className="mt-6 text-[#475569]">This report link is invalid or has been revoked.</p>
        </div>
      </div>
    );
  }

  const p = data.property;
  const isMoveIn = p.doc_type === "move_in";
  const isMidLease = p.documented_stage === "mid_lease";
  const stageLine = isMidLease
    ? `Condition as of ${fmtDate(p.created_at)} — documentation prepared with TenantSeal.`
    : `${isMoveIn ? "Move-in" : "Move-out"} documentation prepared with TenantSeal.`;
  const overall = data.health?.overall_score || "Pending";
  const isDemo = !!data.is_demo;
  const comparisons = data.comparisons || [];
  const disputeLetter = data.dispute_letter;
  const beforeAfter = data.before_after;

  return (
    <div className="min-h-screen bg-[#EEF2F6]">
      {isDemo && (
        <div data-testid="demo-banner" className="sticky top-0 z-40 bg-amber text-navy">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-2.5 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-sm font-semibold text-center">
            <span className="inline-flex items-center gap-1.5"><Sparkles size={15} /> This is a sample report.</span>
            <a href="/login" data-testid="demo-banner-cta" className="inline-flex items-center gap-1 underline underline-offset-2 hover:opacity-80">
              Document your own place free <ArrowRight size={14} />
            </a>
          </div>
        </div>
      )}
      <header className={`${isDemo ? "" : "sticky top-0"} z-20 border-b border-[#E2E8F0] bg-white/85 backdrop-blur-xl`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Logo />
          <a href={`${API}/public/report/${token}/evidence.pdf`} target="_blank" rel="noreferrer" data-testid="public-download-pdf">
            <Button variant="outline" className="rounded-xl border-[#E2E8F0]"><Download size={16} className="mr-2" /> Download PDF</Button>
          </a>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl border border-[#E2E8F0] bg-white p-6">
          <div className="flex items-center gap-2 mb-1">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-[#E2E8F0] bg-[#FAF7F2] px-2.5 py-0.5 text-xs font-semibold text-[#475569]">
              <ShieldCheck size={13} className="text-brand" /> Read-only condition report
            </span>
            {p.is_locked && (
              <SealedStamp lockedAt={p.locked_at} />
            )}
          </div>
          <div className="flex items-start gap-2 mt-2">
            <MapPin size={22} className="text-brand mt-0.5 shrink-0" />
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-navy leading-tight">{p.address}</h1>
          </div>
          <p className="mt-2 text-sm text-[#475569]" data-testid="public-stage-line">{stageLine}</p>

          <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Deposit" value={p.deposit_amount != null ? `$${p.deposit_amount.toLocaleString()}` : "—"} icon={DollarSign} />
            <Stat label="Move-in" value={fmtDate(p.move_in_date)} icon={CalendarClock} />
            <Stat label="Move-out" value={fmtDate(p.move_out_date)} icon={CalendarClock} />
            <div className="rounded-xl border border-[#E2E8F0] bg-[#FAF7F2] p-3">
              <div className="flex items-center gap-1.5 text-[#94A3B8]"><HeartPulse size={14} /><span className="text-[11px] font-semibold uppercase tracking-wider">Health</span></div>
              <span className={`mt-1.5 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${SCORE_STYLE[overall]}`}>{overall}</span>
            </div>
          </div>
          <p className="mt-4 text-xs text-[#94A3B8]">This report describes the observed condition of the property only and does not assign fault or responsibility for any condition noted.</p>
        </motion.div>

        {/* Health per room */}
        {data.health?.rooms?.length > 0 && (
          <div className="mt-6 rounded-2xl border border-[#E2E8F0] bg-white p-6">
            <h2 className="font-heading text-lg font-semibold text-navy">Condition summary</h2>
            <div className="mt-3 space-y-2">
              {data.health.rooms.map((r) => (
                <div key={r.room_id} className="flex items-center justify-between rounded-xl bg-[#FAF7F2] border border-[#E2E8F0] px-4 py-3">
                  <span className="flex items-center gap-2 font-medium text-navy"><DoorOpen size={16} className="text-brand" /> {r.room_name}
                    <span className="text-xs text-[#94A3B8]">· {r.photo_count} photos · {r.issue_count} issues</span></span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${SCORE_STYLE[r.score]}`}>{r.score}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Rooms + photos */}
        <div className="mt-6 space-y-6">
          {data.rooms.map((room) => (
            <div key={room.room_id} className="rounded-2xl border border-[#E2E8F0] bg-white p-6">
              <div className="flex items-center gap-2">
                <DoorOpen size={18} className="text-brand" />
                <h3 className="font-heading text-lg font-semibold text-navy">{room.name}</h3>
                <span className="text-sm text-[#94A3B8]">· {room.photos.length} photos</span>
              </div>
              <div className="mt-4 grid sm:grid-cols-2 gap-4">
                {room.photos.map((ph) => (
                  <div key={ph.photo_id} className="rounded-xl border border-[#E2E8F0] overflow-hidden">
                    <img src={`${API}${ph.image_path}`} alt="" loading="lazy" decoding="async" className="w-full h-48 object-cover" />
                    <div className="p-3">
                      <div className="flex items-center justify-between">
                        <ConditionBadge condition={ph.ai_condition} />
                        <span className="text-xs text-[#94A3B8]">{fmtDate(ph.created_at)}</span>
                      </div>
                      {ph.ai_summary && <p className="mt-2 text-sm text-[#334155]">{ph.ai_summary}</p>}
                      {ph.ai_issues?.length > 0 && (
                        <p className="mt-1 text-xs text-[#92400E] flex items-start gap-1"><AlertTriangle size={12} className="mt-0.5 shrink-0" /> {ph.ai_issues.join(" · ")}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {(beforeAfter || comparisons.length > 0) && (
          <div className="mt-6 rounded-2xl border border-[#E2E8F0] bg-white p-6" data-testid="public-comparison">
            <div className="flex items-center gap-2">
              <Scale size={18} className="text-brand" />
              <h2 className="font-heading text-lg font-semibold text-navy">Move-in vs move-out comparison</h2>
            </div>
            {beforeAfter && (
              <div className="mt-4">
                <BeforeAfterSlider beforeSrc={`${API}${beforeAfter.before_image_path}`} afterSrc={`${API}${beforeAfter.after_image_path}`} />
                <p className="mt-2 text-xs text-[#94A3B8] text-center">{beforeAfter.room_name} · drag to compare</p>
              </div>
            )}
            <div className="mt-4 space-y-3">
              {comparisons.map((c, i) => (
                <div key={c.room_name} className="rounded-xl border border-[#E2E8F0] bg-[#FAF7F2] p-4" data-testid={`comparison-${i}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-navy flex items-center gap-2"><DoorOpen size={15} className="text-brand" /> {c.room_name}</span>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${RISK_STYLE[c.deduction_risk] || RISK_STYLE.none}`}>Deduction risk: {c.deduction_risk}</span>
                  </div>
                  {c.differences?.length > 0 && (
                    <ul className="mt-2 text-sm text-[#334155] list-disc list-inside space-y-0.5">
                      {c.differences.map((d, j) => <li key={`${c.room_name}-${j}`}>{d}</li>)}
                    </ul>
                  )}
                  {c.explanation && <p className="mt-2 text-sm text-[#475569]">{c.explanation}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {disputeLetter && (
          <div className="mt-6 rounded-2xl border border-[#E2E8F0] bg-white p-6" data-testid="public-dispute-letter">
            <div className="flex items-center gap-2">
              <FileText size={18} className="text-brand" />
              <h2 className="font-heading text-lg font-semibold text-navy">Sample dispute letter</h2>
            </div>
            <p className="mt-1 text-sm text-[#475569]">AI-drafted from the comparison findings above.</p>
            <pre className="mt-4 whitespace-pre-wrap font-sans text-sm text-[#334155] bg-[#FAF7F2] border border-[#E2E8F0] rounded-xl p-4 leading-relaxed">{disputeLetter}</pre>
          </div>
        )}

        <AcknowledgeReceipt endpoint={`/public/report/${token}/acknowledge`} initialAcknowledgedAt={p.acknowledged_at} />

        <footer className="mt-10 py-6 text-center">
          <p className="text-sm text-[#94A3B8]">Generated by TenantSeal · condition documentation only</p>
        </footer>
      </main>
    </div>
  );
}
function Stat({ label, value, icon: Icon }) {
  return (
    <div className="rounded-xl border border-[#E2E8F0] bg-[#FAF7F2] p-3">
      <div className="flex items-center gap-1.5 text-[#94A3B8]"><Icon size={14} /><span className="text-[11px] font-semibold uppercase tracking-wider">{label}</span></div>
      <p className="mt-1 font-heading text-base font-bold text-navy truncate">{value}</p>
    </div>
  );
}
